import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CRMLayout } from "@/components/CRMLayout";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, MessageSquare, Pause, Play, Save, Square, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const JOURS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const COLLECTES = {
  "": "Aucune (consigne seule)",
  dev: "Projets, commits et tâches",
  pub: "Campagnes Meta · 7 jours",
  "pub:30j": "Campagnes Meta · 30 jours",
};

function quand(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function Planning({ fil, token, onFerme, onEnregistre }) {
  const p = fil.planning || {};
  const [actif, setActif] = useState(p.actif ?? true);
  const [heure, setHeure] = useState(p.heure || "07:00");
  const [jours, setJours] = useState(p.jours || [0, 1, 2, 3, 4]);
  const [collecte, setCollecte] = useState(p.collecte || "");
  const [consigne, setConsigne] = useState(p.consigne || "");
  const options = { ...COLLECTES, ...(collecte && !COLLECTES[collecte] ? { [collecte]: collecte.replace("compta:", "Comptes Odoo · ") } : {}) };

  const enregistrer = async () => {
    const r = await fetch(`${API_URL}/api/team/missions/${fil.id}/planning`, {
      method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ actif, heure, jours, collecte: collecte || null, consigne }),
    });
    if (!r.ok) return toast.error((await r.json()).detail || "Enregistrement impossible");
    toast.success("Planning enregistré");
    onEnregistre();
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={onFerme}>
      <aside className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{fil.title}</h2>
          <button onClick={onFerme} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} /> Point automatique activé
        </label>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-slate-600">Heure (Réunion)</span>
          <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {JOURS.map((j, i) => (
            <button key={j} onClick={() => setJours(jours.includes(i) ? jours.filter((x) => x !== i) : [...jours, i])}
              className={`rounded-lg px-2.5 py-1 text-sm ${jours.includes(i) ? "bg-indigo-600 text-white" : "border border-slate-200 text-slate-600"}`}>{j}</button>
          ))}
        </div>
        <label className="mt-4 block text-sm text-slate-600">Données collectées par NovaForge</label>
        <select value={collecte} onChange={(e) => setCollecte(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="mt-4 block text-sm text-slate-600">Consigne du point</label>
        <textarea value={consigne} onChange={(e) => setConsigne(e.target.value)} rows={7} className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm" />
        <button onClick={enregistrer} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
          <Save className="h-4 w-4" /> Enregistrer
        </button>
      </aside>
    </div>
  );
}

export default function AgentsPermanentsPage() {
  const { token, user } = useAuth();
  const [agents, setAgents] = useState(null);
  const [edition, setEdition] = useState(null);
  const [ouvert, setOuvert] = useState(null);

  const charger = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/team/agents-permanents`, { headers: { Authorization: `Bearer ${token}` } });
    setAgents(r.ok ? await r.json() : []);
  }, [token]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 20000);          // suit les points en cours
    return () => clearInterval(t);
  }, [charger]);

  const arreter = async (fil) => {
    await fetch(`${API_URL}/api/team/missions/${fil.id}/arreter`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    });
    toast.success("Point arrêté");
    setTimeout(charger, 1000);
  };

  const lancer = async (fil) => {
    const r = await fetch(`${API_URL}/api/team/missions/${fil.id}/executer`, {
      method: "POST", headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return toast.error("Lancement impossible");
    toast.success(`Point lancé : « ${fil.title} ». Résultat dans une à deux minutes.`);
    setTimeout(charger, 1500);
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <h1 className="text-2xl font-bold text-slate-900">Agents permanents</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Chaque agent travaille dans plusieurs fils en parallèle. À l'heure prévue, NovaForge collecte les données
          du fil (Odoo, Meta, projets), l'agent fait le point dans la continuité du fil et propose des actions :
          elles arrivent dans <Link to="/dashboard/tasks?focus=validation" className="font-medium text-indigo-600">Tâches › À valider</Link>.
          Rien n'est exécuté sans ton accord.
        </p>

        {!agents ? <Loader2 className="mt-8 h-6 w-6 animate-spin text-slate-400" /> : (
          <div className="mt-6 space-y-6">
            {agents.filter((a) => a.fils.length).map((a) => (
              <section key={a.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">{a.icon || "🤖"}</span>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-slate-900">{a.name}</h2>
                    <p className="line-clamp-2 text-sm text-slate-500">{a.mission}</p>
                  </div>
                  <Link to="/dashboard/atelier" className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                    <MessageSquare className="h-4 w-4" /> Discuter
                  </Link>
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {a.fils.map((f) => {
                    const ex = f.execution || {};
                    const p = f.planning || {};
                    return (
                      <div key={f.id} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-slate-900">{f.title}</h3>
                          {f.a_valider > 0 && (
                            <Link to="/dashboard/tasks?focus=validation" className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800">
                              {f.a_valider} à valider
                            </Link>
                          )}
                        </div>
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                          <CalendarClock className="h-3.5 w-3.5" />
                          {p.actif ? `${(p.jours || []).map((j) => JOURS[j]).join(" ")} à ${p.heure} · prochain ${quand(f.prochaine_execution)}` : "Pas de point automatique"}
                        </p>
                        {f.en_cours || ex.statut === "en_cours" ? (
                          <div className="mt-3 flex items-center gap-3">
                            <p className="inline-flex items-center gap-1.5 text-sm text-indigo-600"><Loader2 className="h-4 w-4 animate-spin" /> Point en cours…</p>
                            <button onClick={() => arreter(f)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                              <Square className="h-3 w-3 fill-current" /> Arrêter
                            </button>
                          </div>
                        ) : ex.statut === "erreur" ? (
                          <p className="mt-3 flex items-start gap-1.5 text-sm text-red-600"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{ex.erreur}</p>
                        ) : ex.resume ? (
                          <button onClick={() => setOuvert(f)} className="mt-3 block w-full text-left">
                            <p className="flex items-center gap-1.5 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Dernier point {quand(ex.fin)} · {ex.actions || 0} action(s) proposée(s)</p>
                            <p className="mt-1 line-clamp-3 text-sm text-slate-700">{ex.resume}</p>
                          </button>
                        ) : <p className="mt-3 text-sm text-slate-400">Aucun point pour l'instant.</p>}
                        <div className="mt-3 flex gap-2">
                          {user?.role === "admin" && (
                            <button onClick={() => lancer(f)} disabled={f.en_cours}
                              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
                              <Play className="h-3.5 w-3.5" /> Faire le point maintenant
                            </button>
                          )}
                          <button onClick={() => setEdition(f)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">
                            {p.actif ? <CalendarClock className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />} Planning & consigne
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
            {agents.some((a) => !a.fils.length) && (
              <p className="text-sm text-slate-500">
                Autres agents sans fil planifié : {agents.filter((a) => !a.fils.length).map((a) => a.name).join(", ")}.
                Ajoute-leur un fil dans Tâches › Missions.
              </p>
            )}
          </div>
        )}
      </div>

      {edition && <Planning fil={edition} token={token} onFerme={() => setEdition(null)} onEnregistre={() => { setEdition(null); charger(); }} />}
      {ouvert && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30" onClick={() => setOuvert(null)}>
          <aside className="h-full w-full max-w-xl overflow-y-auto bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{ouvert.title}</h2>
              <button onClick={() => setOuvert(null)} className="rounded-lg p-1.5 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Point du {quand(ouvert.execution?.fin)}</p>
            <div className="mt-4 whitespace-pre-wrap text-sm text-slate-800">{ouvert.execution?.resume}</div>
            <p className="mt-4 text-xs text-slate-500">La conversation complète du fil est dans l'onglet Agents (Atelier) : tu peux y répondre à l'agent.</p>
          </aside>
        </div>
      )}
    </CRMLayout>
  );
}
